import { message as antdMessage } from 'antd';
import axios from 'axios';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import type { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest
} from 'redux-saga/effects';

import { samplePointClient } from 'clients';
import { setBackOfficeDevice } from 'store/modules/backOfficeDevice/actions';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import { GetSamplePointDto } from 'types/dto/sample-point.dto';
import Device from 'types/models/device';
import SamplePoint from 'types/models/samplePoint';
import { APIResponseParsed } from 'types/response';
import {
  getRequest,
  patchRequest
} from 'utils/redux-saga-requests';

import {
  editBackOfficeDevice,
  editBackOfficeDeviceFailure,
  editBackOfficeDeviceSuccess,
  loadBackOfficeDeviceCameraSamplePointsFailure,
  loadBackOfficeDeviceCameraSamplePointsSuccess,
  loadBackOfficeDevices,
  loadBackOfficeDevicesFailure,
  loadBackOfficeDevicesSuccess,
  setBackOfficeDevices
} from './actions';
import ActionTypes from './constants';
import { selectBackOfficeDeviceCameraSamplePoints } from './selectors';
import { BackOfficeDeviceCameraFilterValues } from './types';

/**
 * Get array of all camera samplepoints.
 */
export function* getCameraSamplePoints() {
  // If the request for the camera samplepoints is still in progress, we need to
  // wait for it to finish.
  const cameraSamplePointsAreLoading: boolean = yield select(
    makeSelectIsLoading(['LOAD_BACK_OFFICE_DEVICE_CAMERA_SAMPLEPOINTS'])
  );

  if (cameraSamplePointsAreLoading) {
    yield take([
      ActionTypes.LOAD_BACK_OFFICE_DEVICE_CAMERA_SAMPLEPOINTS_SUCCESS,
      ActionTypes.LOAD_BACK_OFFICE_DEVICE_CAMERA_SAMPLEPOINTS_FAILURE
    ]);
  }

  const cameraSamplePoints: SamplePoint[] = yield select(
    selectBackOfficeDeviceCameraSamplePoints
  );

  return cameraSamplePoints;
}

export function* requestBackOfficeDevices(
  action: ReturnType<typeof loadBackOfficeDevices>
): SagaIterator {
  const { parameters } = action.payload;

  try {
    // Need to sort by createdAt DESC by default, but this can be overridden
    // by route query params. Ant design's columns allow column headers to
    // set the route query params for sorting, but their 'defaultSortOrder'
    // prop doesn't do this. As such, we need to add that functionality
    // ourselves here.
    const paramsWithDefaultSortOrder = {
      ...parameters,
      ...(parameters.sort
        ? {
          sort: parameters.sort.some((sortValue: string) => sortValue.includes('createdAt'))
            ? parameters.sort
            : [...parameters.sort, 'createdAt,DESC'] // Sort by createdAt last
        } : {})
    };

    // Check if the 'camera' filter condition exists.
    const cameraParams = paramsWithDefaultSortOrder.s?.$and.find(
      ({ camera }: any) => !!camera
    ) as { camera?: { $in?: string[] }; id?: unknown } | undefined;

    // If it does, we need to get a list of all deviceIds that have cameras, and
    // filter by that.
    if (cameraParams) {
      const cameraSamplePoints = yield* getCameraSamplePoints();
      const withCamera = cameraParams.camera?.$in?.pop() === BackOfficeDeviceCameraFilterValues.WITH_CAMERA;

      cameraParams.id = {
        [withCamera ? '$in' : '$notin']: [
          ...new Set(cameraSamplePoints.map(({ deviceId }) => deviceId))
        ]
      };

      delete cameraParams.camera;
    }

    const { data } = yield call(
      getRequest,
      'device?join=site&join=enterprise',
      { params: paramsWithDefaultSortOrder }
    );

    yield all([
      put(loadBackOfficeDevicesSuccess(data)),
      put(
        setBackOfficeDevices({
          total: data.total,
          data: keyBy(data.data, 'id'),
          ids: data.data.map((device: Device) => device.id)
        })
      )
    ]);
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;

    const message =
      error.message === 'Network Error'
        ? error.message
        : get(error, 'response.data.message', 'Sorry, something went wrong.');

    yield put(loadBackOfficeDevicesFailure(message, error));
  }
}

export function* watchLoadBackOfficeDevicesRequest() {
  yield takeLatest(
    ActionTypes.LOAD_BACK_OFFICE_DEVICES_REQUEST,
    requestBackOfficeDevices
  );
}

export function* requestEditBackOfficeDevice(
  action: ReturnType<typeof editBackOfficeDevice>
) {
  const {
    payload: {
      deviceId,
      values,
      onSuccess
    }
  } = action;

  try {
    const { data } = yield call(patchRequest, `device/${deviceId}`, {
      ...values
    });

    yield all([
      put(setBackOfficeDevice(data)),
      put(editBackOfficeDeviceSuccess(data))
    ]);

    if (onSuccess) onSuccess();
    antdMessage.success('Device edited');
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );

    antdMessage.error('Failed to edit device');
    yield put(editBackOfficeDeviceFailure(message, error));
  }
}

export function* watchEditBackOfficeDevicesRequest() {
  yield takeLatest(
    ActionTypes.EDIT_BACK_OFFICE_DEVICE_REQUEST,
    requestEditBackOfficeDevice
  );
}

export function* requestBackOfficeDeviceCameraSamplePoints() {
  const response: APIResponseParsed<GetSamplePointDto[]> = yield call(samplePointClient.fetchCameraSamplePoints);
  if (response.data) {
    yield put(loadBackOfficeDeviceCameraSamplePointsSuccess(response.data));
  } else {
    const message = response.error?.message || 'Failed to retrieve camera sample points';
    antdMessage.error(message);
    yield put(loadBackOfficeDeviceCameraSamplePointsFailure(message));
  }
}

export function* watchLoadBackOfficeDeviceCameraSamplePointsRequest() {
  yield takeLatest(
    ActionTypes.LOAD_BACK_OFFICE_DEVICE_CAMERA_SAMPLEPOINTS_REQUEST,
    requestBackOfficeDeviceCameraSamplePoints
  );
}

export default function* backOfficeDevicesSaga() {
  yield all([
    fork(watchLoadBackOfficeDevicesRequest),
    fork(watchEditBackOfficeDevicesRequest),
    fork(watchLoadBackOfficeDeviceCameraSamplePointsRequest)
  ]);
}
